import axios from "axios";
import { useCallback, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useAsync } from "react-use";
import { Box, CircularProgress } from "@mui/material";
import Layout from "Components/Layout/Layout";
import VehiclesNavigation from "Navigation/VehiclesNavigation";
import FormWizard, { Step } from "Components/FormWizard";
import ask from "Dialogs/ask";
import showMessage from "Dialogs/showMessage";
import DAP08Pickup from "Pages/Vehicles/Shipping/DAP/DAP08Pickup";
import DAP09Signatures from "Pages/Vehicles/Shipping/DAP/DAP09Signatures";
import { CollectionSessionElement, PickupDetails } from "system/Domain";
import shipmentFormPreDeliveryValid from "./shipmentFormPreDeliveryValid";

interface FinalizeData {
	pickupDetails: PickupDetails;
	isReadonly: () => boolean;
}

const steps: Step[] = [
	{ title: "Fahrzeugabholung", ui: stepProps => <DAP08Pickup {...stepProps} /> },
	{ title: "Unterschriften", ui: stepProps => <DAP09Signatures {...stepProps} /> }
];

// Fills in the shared collector/pickup data ONCE for the whole collection session, then applies
// (copies) it to every open shipment-form in the session and closes them.
const CollectionSessionFinalizePage = () => {
	const { id } = useParams<{ id: string }>();
	const history = useHistory();

	const [readyElements, setReadyElements] = useState<CollectionSessionElement[] | null>();

	useAsync(async () => {
		const { data } = await axios.get<CollectionSessionElement[]>(`/api/sales/collection-sessions/${id}/elements`);

		const active = data.filter(e => !e.member.dateRemoved);
		const allReady = active.length > 0 && active.every(e => {
			const forms = e.sale.shipping?.shipmentForms;
			return shipmentFormPreDeliveryValid(forms?.[forms.length - 1]);
		});

		if (!allReady) {
			await showMessage({
				title: "Nicht möglich",
				message: "Es sind noch nicht alle Fahrzeuge dieser Sitzung für die Abholung vorbereitet (Schritte 1-7).",
				color: "error"
			});
			history.push(`/vehicles/collection-sessions/${id}`);
			return;
		}

		setReadyElements(active);
	}, [id]);

	const dataObject: FinalizeData = useMemo(() => ({
		pickupDetails: {},
		isReadonly: () => false
	}), []);

	const noopSave = useCallback(async () => {
	}, []);

	const complete = useCallback(async (d: FinalizeData) => {
		const { yes } = await ask(`Wollen Sie die Abholung für ${readyElements?.length ?? 0} Fahrzeug(e) wirklich abschließen?`);

		if (!yes) return;

		await axios.post(`/api/sales/collection-sessions/${id}/complete`, d.pickupDetails);

		await showMessage({ title: "Erledigt", message: "Die Sammelsitzung wurde abgeschlossen." });
		history.push(`/vehicles/collection-sessions/${id}`);
	}, [id, readyElements, history]);

	return (
		<Layout title="Abholung abschließen (Sammelsitzung)" navigation={<VehiclesNavigation />}>
			<Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, minHeight: 0, height: "100%" }}>
				{!readyElements && (
					<CircularProgress size={48} />
				)}
				{readyElements && (
					<FormWizard
						dataObject={dataObject}
						steps={steps}
						onSave={noopSave}
						onComplete={complete}
						onReturn={() => history.push(`/vehicles/collection-sessions/${id}`)}
						dirtyFormMessage="Die Änderungen wurden noch nicht übernommen, beim Verlassen der Seite gehen sie verloren."
						stepsProps={null}
					/>
				)}
			</Box>
		</Layout>
	);
};

export default CollectionSessionFinalizePage;
